/**
 * Pure tenant-slug utilities used by the wizard's Step 1 (D1.5.2).
 *
 * Mirrors the canonical Tenant.slug shape (kebab-case [a-z0-9-], length 3-40)
 * defined in `verticals/pet-services/prisma/schema.prisma` and design §2.
 *
 * Lives in admin-app/lib for now; a later step (when intake portal D2 reuses
 * the same suggester) can hoist into `@ferro/config` without changing
 * behavior — these helpers are framework-free.
 */

export const TENANT_SLUG_MIN = 3;
export const TENANT_SLUG_MAX = 40;
const TENANT_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export type SlugInvalid =
  | { kind: 'too_short' }
  | { kind: 'too_long' }
  | { kind: 'bad_chars' }
  | { kind: 'edge_dash' };

export type SlugValidation =
  | { ok: true; slug: string }
  | { ok: false; error: SlugInvalid };

/**
 * Strict slug validation. Length 3-40, kebab-case, no leading/trailing dash.
 * `bad_chars` covers anything outside `[a-z0-9-]`; `edge_dash` covers the
 * leading/trailing-dash case the regex explicitly rejects.
 */
export function validateTenantSlug(raw: string): SlugValidation {
  if (raw.length < TENANT_SLUG_MIN) {
    return { ok: false, error: { kind: 'too_short' } };
  }
  if (raw.length > TENANT_SLUG_MAX) {
    return { ok: false, error: { kind: 'too_long' } };
  }
  if (raw.startsWith('-') || raw.endsWith('-')) {
    return { ok: false, error: { kind: 'edge_dash' } };
  }
  if (!TENANT_SLUG_PATTERN.test(raw)) {
    return { ok: false, error: { kind: 'bad_chars' } };
  }
  return { ok: true, slug: raw };
}

/**
 * Suggest a slug from a free-text display name. Idempotent under repeated
 * application — `suggest(suggest(x)) === suggest(x)` for any input.
 *
 * Steps:
 * 1. Lowercase + strip diacritics (NFD normalize → drop combining marks)
 * 2. Replace any run of non-`[a-z0-9]` with a single dash
 * 3. Trim leading/trailing dashes
 * 4. Truncate to TENANT_SLUG_MAX, then re-trim trailing dash if truncation
 *    landed on one
 *
 * Returns `''` if no usable characters remain (caller decides whether to
 * surface this as an error or just leave the field empty).
 */
export function suggestSlugFromDisplayName(displayName: string): string {
  const lower = displayName.toLowerCase();
  const stripped = lower.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const dashed = stripped.replace(/[^a-z0-9]+/g, '-');
  const trimmed = dashed.replace(/^-+|-+$/g, '');
  if (trimmed.length <= TENANT_SLUG_MAX) return trimmed;
  return trimmed.slice(0, TENANT_SLUG_MAX).replace(/-+$/g, '');
}

export function describeSlugError(err: SlugInvalid): string {
  switch (err.kind) {
    case 'too_short':
      return `Slug must be at least ${TENANT_SLUG_MIN} characters.`;
    case 'too_long':
      return `Slug must be at most ${TENANT_SLUG_MAX} characters.`;
    case 'bad_chars':
      return 'Slug may only contain lowercase letters, numbers, and dashes.';
    case 'edge_dash':
      return 'Slug may not start or end with a dash.';
  }
}
